export enum PaymentFormVariant {
    Default = 'default',
    ElqrV1 = 'elqr_v1',
    ElqrV2 = 'elqr_v2',
    SbpKgsPhoneWith996 = 'sbp_kgs_phone_with_996',
    SbpKgsPhoneWithout996 = 'sbp_kgs_phone_without_996',
    MerchantBranded = 'merchant_branded',
}

export interface PaymentFormAssignment {
    form_variant?: string | null;
    abtest?: string | null;
    form_experiments?: Record<string, {
        form_variant?: string | null;
        abtest?: string | null;
    }>;
}

const assignments: Record<string, string> = {
    elqr_compact_v1_control: PaymentFormVariant.ElqrV1,
    elqr_compact_v1_compact: PaymentFormVariant.ElqrV2,
    sbp_kgs_phone_copy_v1_with_996: PaymentFormVariant.SbpKgsPhoneWith996,
    sbp_kgs_phone_copy_v1_without_996: PaymentFormVariant.SbpKgsPhoneWithout996,
    merchant_branding_v1_control: PaymentFormVariant.Default,
    merchant_branding_v1_branded: PaymentFormVariant.MerchantBranded,
};

export function paymentFormAbtest(data: PaymentFormAssignment): string {
    if (!data.form_variant) return data.abtest ? 'unknown' : 'none';
    if (!Object.values(PaymentFormVariant).includes(data.form_variant as PaymentFormVariant)) return 'unknown';
    if (data.abtest === 'unknown') return 'unknown';
    if (data.abtest && assignments[data.abtest] === data.form_variant) return data.abtest;
    if (data.abtest === 'merchant_branding_v1_control' && data.form_variant === PaymentFormVariant.ElqrV2) {
        return data.abtest;
    }
    if ((!data.abtest || data.abtest === 'none') && [PaymentFormVariant.Default, PaymentFormVariant.ElqrV1,
        PaymentFormVariant.ElqrV2, PaymentFormVariant.SbpKgsPhoneWith996, PaymentFormVariant.MerchantBranded].includes(data.form_variant as PaymentFormVariant)) return 'none';
    return 'unknown';
}

export const isCompactElqr = (data: PaymentFormAssignment & { requisite_type?: string | null; order_details?: { order_type?: string } } | null) =>
    Boolean(data?.requisite_type === 'elqr' && data.order_details?.order_type === 'deposit'
        && (data.form_experiments?.elqr_compact_v1?.form_variant === PaymentFormVariant.ElqrV2
            || ([PaymentFormVariant.ElqrV2, PaymentFormVariant.MerchantBranded].includes(data.form_variant as PaymentFormVariant)
                && paymentFormAbtest(data) !== 'unknown')));

interface SbpKgsPhoneForm extends PaymentFormAssignment {
    requisite_type?: string | null;
    fiat_currency?: string;
    order_details?: { order_type?: string; from_currency?: string };
}

export const isSbpKgsPhoneCopyExperiment = (data: SbpKgsPhoneForm | null): boolean => Boolean(
    data && data.order_details?.order_type === 'deposit'
    && (data.fiat_currency || data.order_details?.from_currency) === 'KGS'
    && ['sbp', 'sbp_kgs'].includes(data.requisite_type || '')
    && ['sbp_kgs_phone_copy_v1_with_996', 'sbp_kgs_phone_copy_v1_without_996'].includes(
        data.form_experiments?.sbp_kgs_phone_copy_v1?.abtest
            || (['sbp_kgs_phone_copy_v1_with_996', 'sbp_kgs_phone_copy_v1_without_996'].includes(data.abtest || '')
                ? data.abtest! : ''),
    )
);

export const paymentMethodAbtest = (data: PaymentFormAssignment & { requisite_type?: string | null } | null) => {
    if (!data) return 'none';
    const name = data.requisite_type === 'elqr' ? 'elqr_compact_v1'
        : ['sbp', 'sbp_kgs'].includes(data.requisite_type || '') ? 'sbp_kgs_phone_copy_v1' : null;
    const assignedAbtest = name ? data.form_experiments?.[name]?.abtest : undefined;
    if (assignedAbtest) return assignedAbtest;
    return ['elqr_compact_v1_control', 'elqr_compact_v1_compact',
        'sbp_kgs_phone_copy_v1_with_996', 'sbp_kgs_phone_copy_v1_without_996'].includes(data.abtest || '')
        ? data.abtest! : 'none';
};

export function sbpKgsPhoneClipboard(text: string, data: SbpKgsPhoneForm | null) {
    const digits = text.replace(/\D/g, '');
    // Only recognized Kyrgyz numbers participate; never truncate an unfamiliar number.
    const national = /^996\d{9}$/.test(digits) ? digits.slice(3)
        : /^00996\d{9}$/.test(digits) ? digits.slice(5)
            : /^0\d{9}$/.test(digits) ? digits.slice(1)
                : /^\d{9}$/.test(digits) ? digits : null;
    const assignedVariant = data?.form_experiments?.sbp_kgs_phone_copy_v1?.form_variant ?? data?.form_variant;
    const copied = national && isSbpKgsPhoneCopyExperiment(data)
        ? assignedVariant === PaymentFormVariant.SbpKgsPhoneWithout996 ? national : `996${national}`
        : digits;
    return {
        text: copied,
        format: /^996\d{9}$/.test(copied) ? 'with_996'
            : /^\d{9}$/.test(copied) ? 'without_996' : 'unchanged',
    };
}
